import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useUIStore } from '../../../stores/useUIStore';
import { cn } from '../../../utils/cn';
import SidebarHeader from './SidebarHeader.jsx';
import SidebarNav from './SidebarNav.jsx';
import SidebarFooter from './SidebarFooter.jsx';

const Sidebar = () => {
    const { isSidebarOpen, setSidebarOpen, toggleSidebar } = useUIStore();
    const location = useLocation();

    // Close sidebar on route change (mobile)
    useEffect(() => {
        if (window.innerWidth < 1024) {
            setSidebarOpen(false);
        }
    }, [location.pathname, setSidebarOpen]);

    const sidebarVariants = {
        expanded: {
            width: 280,
            transition: {
                type: "spring",
                stiffness: 260,
                damping: 26,
                mass: 0.6,
                restDelta: 0.01
            }
        },
        collapsed: {
            width: 88,
            transition: {
                type: "spring",
                stiffness: 260,
                damping: 26,
                mass: 0.6,
                restDelta: 0.01
            }
        }
    };

    return (
        <>
            {/* Mobile Overlay */}
            <AnimatePresence>
                {isSidebarOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
                        onClick={() => setSidebarOpen(false)}
                    />
                )}
            </AnimatePresence>

            {/* Sidebar Container */}
            <motion.aside
                layout
                variants={sidebarVariants}
                initial={false}
                animate={isSidebarOpen ? "expanded" : "collapsed"}
                className={cn(
                    "fixed inset-y-0 left-0 z-50 flex flex-col h-full bg-white dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800 shadow-xl lg:shadow-none lg:static",
                    // Mobile handling: always show if open (slide in), hide if closed
                    isSidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
                    "transition-transform duration-300 lg:transition-none" // Only apply transform transition on mobile
                )}
            >
                {/* Background Texture/Gradient */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-r-3xl">
                    <div className="absolute top-0 left-0 w-full h-[500px] bg-gradient-to-b from-blue-500/5 via-purple-500/5 to-transparent opacity-50" />
                    <div className="absolute -top-[200px] -left-[200px] w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-3xl mix-blend-multiply filter" />
                    <div className="absolute top-[20%] -right-[100px] w-[300px] h-[300px] bg-purple-400/10 rounded-full blur-3xl mix-blend-multiply filter" />

                    {/* Glassmorphism Effect Enhancement */}
                    <div className="absolute inset-0 bg-white/5 dark:bg-black/5 backdrop-filter backdrop-blur-[1px]" />
                </div>

                {/* Content */}
                <div className="relative z-10 flex flex-col h-full overflow-hidden">
                    <SidebarHeader
                        collapsed={!isSidebarOpen}
                        toggleSidebar={toggleSidebar}
                        onClose={() => setSidebarOpen(false)}
                    />

                    <SidebarNav
                        collapsed={!isSidebarOpen}
                        onItemClick={() => {
                            if (window.innerWidth < 1024) setSidebarOpen(false);
                        }}
                    />

                    <SidebarFooter collapsed={!isSidebarOpen} />
                </div>
            </motion.aside>
        </>
    );
};

export default Sidebar;
